import React, { createContext, ReactNode, use } from 'react'
import { usePublicStyling } from '@chilipiper/api-queries/src/workspace'
import * as R from 'remeda'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { PiperAiLogoView } from '@chilipiper/api-client/src/chat-platform-piper-ai'
import { Box, styled } from '@chilipiper/design-system'
import { PersonalDetails } from '@chilipiper/api-client/src/workspace'
import { SvgAgent } from '@chilipiper/icons'
import { getName } from '../helpers/getName'
import { Context } from '../components/types'
import { useGetAppearanceSettings } from '../api-queries/botAndDataFieldsVisibilitySettings'
import { useTenantDetails } from '../api-queries/workspace'
import { usePersonalDetails } from '../api-queries/personal-details'
import { BlockOrCompositeBlock } from '../components/compositeBlocks'
import { usePublicPersonalitySettings } from '../api-queries/piper-ai'
import { useMessageFeed } from './useMessageFeed'

export const MessageFeedContext = createContext<Context>({
  sendingMessage: false,
  sendMessage: () => undefined,
  prepareChat: () => Promise.resolve(),
  disableInteractions: false,
  workspaceId: '',
  pickReplyButton: () => undefined,
  submitForm: () => undefined,
  triggerMeetingFallback: () => undefined,
  isTyping: false,
  isLoading: false,
  reconnectingStatus: 'CONNECTED',
  blocks: [],
  selectMeetingSlot: () => undefined,
  tenantId: '',
  isNarrowScreen: false,
  piperAiActive: false,
  sendMessageToPiperAi: () => undefined,
  isWaitingForPiperAiResponse: false,
  isSessionExpired: false,
})

const emptyContext: Context = {
  channel: undefined,
  sendingMessage: false,
  pickReplyButton: () => undefined,
  submitForm: () => undefined,
  blocks: [],
  liveChatActive: false,
  isTyping: false,
  isPreview: false,
  disableInteractions: false,
  isLoading: false,
  workspaceId: '',
  selectMeetingSlot: () => undefined,
  triggerMeetingFallback: () => undefined,
  reconnectingStatus: 'CONNECTED' as const,
  sendMessage: () => undefined,
  prepareChat: () => Promise.resolve(),
  tenantId: '',
  roomId: 'roomId',
  isNarrowScreen: false,
  piperAiActive: false,
  sendMessageToPiperAi: () => undefined,
  isWaitingForPiperAiResponse: false,
  isSessionExpired: false,
}

export const MockMessageFeedProvider = ({
  children,
  name,
  tenantId,
  messages,
  logo,
}: {
  children?: ReactNode
  messages?: Array<BlockOrCompositeBlock>
  name?: string
  tenantId: string
  logo?: string
}) => {
  const orgDetails = {
    name,
    logo,
  }

  const channel = {
    type: 'bot' as const,
    fullName: orgDetails.name ?? '',
    avatar: orgDetails.logo,
  }

  return (
    <MessageFeedContext
      value={{
        ...emptyContext,
        blocks: messages ?? [],
        tenantId,
        guestId: 'user',
        channel,
        orgDetails,
        aiDetails: {
          name: 'Chat AI',
          logo: getLogo({ type: 'DefaultLogo' }),
        },
      }}
    >
      {children}
    </MessageFeedContext>
  )
}

export const PreviewMessageFeedProvider = ({
  children,
  messages,
  name,
  workspaceId,
}: {
  children: ReactNode
  messages?: Array<BlockOrCompositeBlock>
  name?: string
  workspaceId: string
}) => {
  const { tenantId, userId } = useFireSessionContext()
  const { orgDetails, aiDetails } = useOrgAndAiDetails(tenantId, workspaceId ?? '')

  const previewOrgDetails = {
    ...orgDetails,
    name: name ?? orgDetails.name,
    logo: typeof orgDetails.logo === 'string' ? orgDetails.logo : undefined,
  }

  const channel = {
    type: 'bot' as const,
    fullName: previewOrgDetails.name ?? '',
    avatar: typeof previewOrgDetails.logo === 'string' ? previewOrgDetails.logo : undefined,
  }

  return (
    <MessageFeedContext
      value={{
        ...emptyContext,
        isPreview: true,
        tenantId,
        disableInteractions: true,
        orgDetails: previewOrgDetails,
        aiDetails,
        channel,
        guestId: userId,
        blocks: messages ?? [],
      }}
    >
      {children}
    </MessageFeedContext>
  )
}

interface Props {
  children?: ReactNode
  disableInteractions?: boolean
  onBootstrapComplete?: (roomId: string, playbookId: string) => void
  tenantId: string
  workspaceId?: string
  isNarrowScreen?: boolean
}

export const MessageFeedProvider = ({
  children,
  disableInteractions = false,
  workspaceId,
  tenantId,
  onBootstrapComplete,
  isNarrowScreen = false,
}: Props) => {
  const {
    selectMeetingSlot,
    guestId,
    liveChatActive,
    sendingMessage,
    pickReplyButton,
    submitForm,
    blocks,
    isThrottling,
    sendLiveMessage,
    status,
    prepareChat,
    isLoading,
    roomId,
    triggerMeetingFallback,
    piperAiActive,
    sendMessageToPiperAi,
    isWaitingForPiperAiResponse,
    isSessionExpired,
  } = useMessageFeed({
    workspaceId,
    tenantId,
    onBootstrapComplete,
  })
  const { orgDetails, aiDetails } = useOrgAndAiDetails(tenantId, workspaceId ?? '')

  const assigneeId = R.pipe(
    blocks,
    R.findLast(
      block =>
        block.type === 'SystemChatTransferred' ||
        block.type === 'SystemUserResponded' ||
        (block.type === 'SystemMessage' && block.payload.type === 'ConversationSessionExpired')
    ),
    block => (block?.type === 'SystemUserResponded' ? block.author : undefined)
  )

  const { data: assignee } = usePersonalDetails(tenantId, assigneeId)

  const channel = createChannel({
    assignee,
    aiDetails,
    orgDetails,
    piperAiActive,
    assigneeId,
  })

  const value = {
    guestId,
    channel,
    liveChatActive,
    reconnectingStatus: status,
    sendingMessage,
    pickReplyButton,
    submitForm,
    tenantId,
    blocks,
    isTyping: isThrottling || isWaitingForPiperAiResponse,
    isPreview: false,
    disableInteractions,
    prepareChat,
    sendMessage: piperAiActive ? sendMessageToPiperAi : sendLiveMessage,
    isLoading,
    selectMeetingSlot,
    isNarrowScreen,
    triggerMeetingFallback,
    workspaceId,
    roomId,
    orgDetails,
    piperAiActive,
    sendMessageToPiperAi,
    isWaitingForPiperAiResponse,
    isSessionExpired,
    aiDetails,
  }

  return <MessageFeedContext value={value}>{children}</MessageFeedContext>
}

const createChannel = ({
  assignee,
  aiDetails,
  orgDetails,
  piperAiActive,
  assigneeId,
}: {
  assignee: PersonalDetails | undefined
  aiDetails:
    | {
        name?: string
        logo?: string | React.ReactNode
      }
    | undefined
  orgDetails:
    | {
        name?: string
        logo?: string
      }
    | undefined
  piperAiActive?: boolean
  assigneeId?: string
}) => {
  // Assignee
  if (assigneeId) {
    if (!assignee) {
      return undefined
    }
    return {
      type: 'assignee' as const,
      id: assigneeId,
      fullName: getName(assignee) ?? '',
      avatar: assignee?.imageUrl,
    }
  }

  // Piper AI
  if (piperAiActive) {
    if (!aiDetails) {
      return undefined
    }
    return {
      type: 'piper-ai' as const,
      fullName: aiDetails?.name ?? 'Chat AI',
      avatar: aiDetails.logo,
    }
  }

  // Bot
  if (!orgDetails) {
    return undefined
  }
  return {
    type: 'bot' as const,
    fullName: orgDetails.name ?? '',
    avatar: orgDetails.logo,
  }
}

export const useMessageFeedContext = () => {
  return use(MessageFeedContext)
}

const getLogo = (logo?: PiperAiLogoView, botLogo?: string, actionColor?: string) => {
  if (logo?.type === 'CustomLogo') {
    return logo?.logoUrl
  }
  if (logo?.type === 'BotLogo') {
    return botLogo
  }
  return (
    // Percentages so we reach 16px width of the circle inside the icon but keep the original size wherever it's used
    <StyledIconWrapper hexColor={actionColor} m='-41.5%' w='183%' h='183%'>
      <SvgAgent />
    </StyledIconWrapper>
  )
}

const StyledIconWrapper = styled(Box)<{ hexColor?: string }>`
  color: ${({ hexColor }) => hexColor};
`

const useOrgAndAiDetails = (tenantId: string, workspaceId: string) => {
  const { data: tenantDetails } = useTenantDetails(tenantId)
  const { appearanceSettings } = useGetAppearanceSettings(tenantId, workspaceId)
  const { data } = usePublicStyling(tenantId, workspaceId)
  const { data: personalitySettings } = usePublicPersonalitySettings(tenantId, workspaceId)
  return {
    orgDetails: {
      logo: data?.logo,
      name: appearanceSettings?.settings.companyName || tenantDetails?.payload.companyName,
    },
    aiDetails: {
      logo: getLogo(
        personalitySettings?.logo,
        data?.logo,
        appearanceSettings?.settings.actionColor
      ),
      name: personalitySettings?.piperAiBotName,
    },
  }
}
